import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { SKINS, type ArtKey, type SkinManifest } from '../skins/index.js';
import {
  BASE_SKIN,
  LOOK_KEY,
  parseLook,
  skinForRoute,
  type SkinRoute,
  type StudentLook,
} from '../ui/skin-scope.js';

/**
 * 皮肤 store:加载清单、把 data-skin 写到 <html>(令牌按属性选择器生效)、给组件提供资产 URL。
 * 页面里用 <SkinArt name="mascot" /> 或 useSkin().art('mascot'),不直接 import 图片。
 *
 * 套哪套只看页面(ui/skin-scope.ts):学生端 / 家长端页面是「深色外壳」shell,其余页面 default。
 * 每次进页面前(main.ts 里的路由守卫)调 `enter`:该换就换,要下载就等它下载完,页面不会先闪一下别的样子。
 */
export const useSkin = defineStore('skin', () => {
  /** 当前真正套在页面上的那套 */
  const id = ref(BASE_SKIN);
  const manifest = ref<SkinManifest | null>(null);
  const ready = computed(() => manifest.value !== null);

  /** 最近一次要套的皮肤(可能还在加载)和它的加载过程:连着要换两次时以最后一次为准,先发的晚到也不会盖回去 */
  let target = '';
  let inflight: Promise<void> = Promise.resolve();

  function load(next: string): Promise<void> {
    const loader = SKINS[next] ?? SKINS[BASE_SKIN]!;
    target = next;
    inflight = loader().then(
      (m) => {
        if (target !== next) return;
        manifest.value = m;
        id.value = m.id;
        document.documentElement.dataset['skin'] = m.id;
      },
      (err: unknown) => {
        // 没加载下来:目标退回现在套着的这套,下次还会再试
        if (target === next) target = manifest.value ? id.value : '';
        throw err;
      },
    );
    return inflight;
  }

  /** 学生端版式(ui/skin-scope.ts):本机记住;地址带 ?look=academy / classic 时切过去并记住 */
  const look = ref<StudentLook>(readLook());
  function readLook(): StudentLook {
    try {
      return parseLook(localStorage.getItem(LOOK_KEY)) ?? 'classic';
    } catch {
      return 'classic';
    }
  }
  function setLook(l: StudentLook) {
    look.value = l;
    try {
      localStorage.setItem(LOOK_KEY, l);
    } catch {
      /* 隐私模式:只在这次打开里生效 */
    }
  }
  let lastRoute: SkinRoute | null = null;
  /** 页面上的「新版 / 旧版」切换:记住并立刻换掉当前页的皮肤 */
  async function switchLook(l: StudentLook): Promise<void> {
    setLook(l);
    if (lastRoute) await enter(lastRoute);
  }

  /** 进这一页之前:套上这一页该用的那套;已经是(或正在换成)那套就只等它就位 */
  async function enter(to: SkinRoute & { query?: Record<string, unknown> }): Promise<void> {
    const asked = parseLook(to.query?.['look']);
    if (asked && asked !== look.value) setLook(asked);
    lastRoute = to;
    const next = skinForRoute(to, look.value);
    try {
      await (next === target ? inflight : load(next));
    } catch {
      // 皮肤没下载下来(断网、发版后旧文件被清):保持现在这套,不挡页面;启动时一套都还没有就退回 default
      if (!manifest.value) await load(BASE_SKIN);
    }
  }

  function art(key: ArtKey): string {
    return manifest.value?.art[key] ?? '';
  }
  const brand = computed(() => manifest.value?.brand ?? '天才喵');
  const brandMark = computed(() => manifest.value?.brandMark ?? '喵');
  const slogan = computed(() => manifest.value?.slogan ?? '');
  const tagline = computed(() => manifest.value?.tagline ?? '');
  const mascotName = computed(() => manifest.value?.mascotName ?? '小智');

  return {
    id,
    manifest,
    ready,
    load,
    enter,
    look,
    switchLook,
    art,
    brand,
    brandMark,
    slogan,
    tagline,
    mascotName,
  };
});
